import { theme } from 'antd';
import type { ThemeConfig } from 'antd';

// ===== Seed Token（全局基础 Token）=====
const seedToken: NonNullable<ThemeConfig['token']> = {
  colorPrimary: '#6932ff',
  colorPrimaryHover: '#c333ff',
  borderRadius: 8,
  fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, sans-serif',
};

// ===== Button Token =====
// 设计稿尺寸定义：Maximum(48) / Large(40) / Medium(32) / Small(24)
// Ant Design 映射：controlHeightLG(48) / controlHeight(40) / controlHeightSM(32)
// 注意：24px 尺寸需要通过自定义 className 实现
const buttonToken: NonNullable<NonNullable<ThemeConfig['components']>['Button']> = {
  // 尺寸 - 对应设计稿 Maximum(48) / Large(40) / Medium(32)
  controlHeightLG: 48,
  controlHeight: 40,
  controlHeightSM: 32,
  // 内边距
  paddingInlineLG: 24,
  paddingInline: 20,
  paddingInlineSM: 16,
  // 圆角 - 48/40 使用 12px，32/24 使用 8px
  borderRadiusLG: 12,
  borderRadius: 12,
  borderRadiusSM: 8,
  // 字号
  fontSizeLG: 16,
  fontSize: 14,
  fontSizeSM: 14,
  // 字重
  fontWeightStrong: 600,
  // 行高
  lineHeightLG: 1.375, // 22/16
  lineHeight: 1.428, // 20/14
  lineHeightSM: 1.428, // 20/14
};

// ===== Input Token =====
const inputToken: NonNullable<NonNullable<ThemeConfig['components']>['Input']> = {
  controlHeightLG: 48,
  controlHeight: 48,
  controlHeightSM: 36,
  borderRadius: 8,
  paddingInline: 16,
  paddingInlineLG: 16,
  paddingInlineSM: 12,
  activeBorderColor: '#c333ff',
  hoverBorderColor: '#c333ff',
  activeShadow: '0 0 0 2px rgba(195, 51, 255, 0.1)',
};

// ===== Select Token =====
const selectToken: NonNullable<NonNullable<ThemeConfig['components']>['Select']> = {
  controlHeightLG: 48,
  controlHeight: 48,
  controlHeightSM: 36,
  borderRadius: 8,
  optionSelectedBg: 'var(--pop-sel)',
  optionActiveBg: 'rgba(96, 97, 108, 0.2)',
};

// ===== Menu Token =====
const menuToken: NonNullable<NonNullable<ThemeConfig['components']>['Menu']> = {
  itemHeight: 48,
  itemBorderRadius: 12,
  subMenuItemBorderRadius: 10,
  itemPaddingInline: 16,
  itemMarginBlock: 4,
  iconSize: 20,
  iconMarginInlineEnd: 12,
  activeBarWidth: 0,
  itemBg: 'transparent',
  subMenuItemBg: 'var(--sidebar-menu-sub-bg)',
  itemColor: 'var(--sidebar-menu-item-color)',
  itemHoverColor: 'var(--sidebar-menu-item-hover-color)',
  itemHoverBg: 'var(--sidebar-menu-item-hover-bg)',
  itemSelectedColor: 'var(--sidebar-menu-item-selected-color)',
  itemSelectedBg: 'var(--sidebar-menu-item-selected-bg)',
  // Submenu 展开/选中状态颜色（覆盖 colorPrimary）
  subMenuItemSelectedColor: 'var(--sidebar-menu-item-color)',
};

// ===== Table Token =====
const tableToken: NonNullable<NonNullable<ThemeConfig['components']>['Table']> = {
  rowHoverBg: 'var(--table-row-hover-bg)',
};

// ===== 暗色模式专用 Token =====
const darkModeToken: NonNullable<ThemeConfig['token']> = {
  colorBgContainer: 'var(--bg-input-pop)',
  colorBgElevated: 'var(--pop-bg)',
  colorBorder: 'var(--border-default)',
  colorText: 'var(--text-primary)',
  colorTextSecondary: 'var(--text-secondary)',
  colorTextPlaceholder: 'var(--text-supelement)',
};

// ===== 亮色模式专用 Token =====
const lightModeToken: NonNullable<ThemeConfig['token']> = {
  colorBgContainer: 'var(--bg-input-pop)',
  colorBgElevated: 'var(--pop-bg)',
  colorBorder: 'var(--border-default)',
  colorText: 'var(--text-primary)',
  colorTextSecondary: 'var(--text-secondary)',
  colorTextPlaceholder: 'var(--text-supelement)',
};

// ===== Auth 页面专用 Token（硬编码亮色值）=====
const authLightToken: NonNullable<ThemeConfig['token']> = {
  colorBgContainer: '#ffffff',
  colorBgElevated: '#ffffff',
  colorBorder: '#eef0f2',
  colorText: 'rgba(0, 0, 0, 0.87)',
  colorTextSecondary: 'rgba(0, 0, 0, 0.6)',
  colorTextPlaceholder: 'rgba(0, 0, 0, 0.38)',
};

// ===== 导出配置 =====
export const getAntdThemeConfig = (isDarkMode: boolean): ThemeConfig => ({
  algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
  token: {
    ...seedToken,
    ...(isDarkMode ? darkModeToken : lightModeToken),
  },
  components: {
    Button: buttonToken,
    Input: inputToken,
    Select: selectToken,
    Menu: menuToken,
    Table: tableToken,
  },
});

// ===== Auth 页面专用配置（强制亮色，不受主题影响）=====
export const getAuthLightThemeConfig = (): ThemeConfig => ({
  algorithm: theme.defaultAlgorithm,
  token: {
    ...seedToken,
    ...authLightToken,
  },
  components: {
    Button: buttonToken,
  },
});
